import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import Header from './Header';
import Sidebar from './Sidebar';
import './Layout.css';

interface LayoutProps {
  children: ReactNode;
}

/**
 * Admin main layout component
 *
 * Layout structure:
 * - Header: Fixed top navigation bar with Logo and user menu
 * - Sidebar: Fixed left sidebar with navigation menu
 * - Main: Main content area rendering different pages based on routes
 *
 * Reference design: OpenRouter (https://openrouter.ai/models)
 */
export default function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);

  // 路由切换时关闭移动端菜单
  useEffect(() => {
    setIsMobileNavOpen(false);
  }, [location.pathname]);

  // 移动端菜单打开时禁用页面滚动
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;

    if (isMobileNavOpen) {
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isMobileNavOpen]);

  return (
    <div className="layout-wrapper">
      {/* Top navigation bar */}
      <Header
        isMobileNavOpen={isMobileNavOpen}
        onMenuClick={() => setIsMobileNavOpen(prev => !prev)}
      />

      {/* Main body area */}
      <div className="layout-body">
        {/* 移动端遮罩层 */}
        <div
          className={`layout-mobile-overlay ${isMobileNavOpen ? 'open' : ''}`}
          onClick={() => setIsMobileNavOpen(false)}
          aria-hidden="true"
        />
        
        {/* Left sidebar */}
        <Sidebar
          isMobileNavOpen={isMobileNavOpen}
          onClose={() => setIsMobileNavOpen(false)}
          onNavigate={() => setIsMobileNavOpen(false)}
        />

        {/* Main content area */}
        <main className="layout-main">{children}</main>
      </div>
    </div>
  );
}

// Export sub-components for standalone use
export { Header, Sidebar };
